import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { AppError } from '@/app/core/http/app-error.model';
import { toAppError } from '@/app/core/http/error-catalog';
import { SettlementAcceptance, SettlementCancellation } from '../models/settlement-action.model';
import { SettlementActionRepository } from '../services/settlement-action.repository';
import { rejectionMessage } from '../utils/action-rejection.util';

export type SettlementAction = 'validate' | 'cancel';
export type SettlementActionStatus = 'idle' | 'submitting' | 'done' | 'rejected' | 'error';

const ERROR_SUBJECTS: Record<SettlementAction, string> = {
  validate: 'la validación de la carga',
  cancel: 'la anulación de la liquidación',
};

const REJECTION_FALLBACKS: Record<SettlementAction, string> = {
  validate: 'La liquidación ya no se puede validar.',
  cancel: 'La liquidación ya no se puede anular.',
};

@Injectable()
export class SettlementActionStore {
  private readonly repository = inject(SettlementActionRepository);

  private readonly _action = signal<SettlementAction | null>(null);
  private readonly _status = signal<SettlementActionStatus>('idle');
  private readonly _result = signal<SettlementAcceptance | SettlementCancellation | null>(null);
  private readonly _rejection = signal<string | null>(null);
  private readonly _error = signal<AppError | null>(null);

  readonly action = this._action.asReadonly();
  readonly status = this._status.asReadonly();
  readonly result = this._result.asReadonly();
  readonly rejection = this._rejection.asReadonly();
  readonly error = this._error.asReadonly();
  readonly isSubmitting = computed(() => this._status() === 'submitting');

  // El store vive en la ruta: sin esto, al volver a la página se repetiría el último resultado.
  reset(): void {
    this._action.set(null);
    this._result.set(null);
    this._rejection.set(null);
    this._error.set(null);
    this._status.set('idle');
  }

  run(action: SettlementAction, settlementId: number): void {
    if (this.isSubmitting()) return;

    this._action.set(action);
    this._result.set(null);
    this._rejection.set(null);
    this._error.set(null);
    this._status.set('submitting');

    const request: Observable<SettlementAcceptance | SettlementCancellation> =
      action === 'validate'
        ? this.repository.validate(settlementId)
        : this.repository.cancel(settlementId);

    request.subscribe({
      next: (result) => {
        this._result.set(result);
        this._status.set('done');
      },
      error: (cause: unknown) => {
        const rejection = rejectionMessage(cause, REJECTION_FALLBACKS[action]);
        if (rejection) {
          this._rejection.set(rejection);
          this._status.set('rejected');
          return;
        }
        this._error.set(toAppError(cause, ERROR_SUBJECTS[action]));
        this._status.set('error');
      },
    });
  }
}
